import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import PublicRoute from "components/PublicRoute";
import PerformanceMonitor from "components/PerformanceMonitor";
import { LoadingSkeleton } from "components/ui/LoadingSkeleton";
import DebtPayoffCalculator from './pages/debt-payoff-calculator';
import RecurringBillTracker from './pages/recurring-bill-tracker';
import RetirementCalculator from './pages/retirement-calculator';
import SkipNavigation from './components/ui/SkipNavigation';

// Lazy load all route components
const NotFound = lazy(() => import("pages/NotFound"));
const TransactionManagement = lazy(() => import('./pages/transaction-management'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const ReportsAnalytics = lazy(() => import('./pages/reports-analytics'));
const InvestmentTracker = lazy(() => import('./pages/investment-tracker'));
const BankConnection = lazy(() => import('./pages/bank-connection'));
const ReceiptScanner = lazy(() => import('./pages/receipt-scanner'));
const Settings = lazy(() => import('./pages/settings'));
const SystemsManagement = lazy(() => import('./pages/systems-management'));
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const Onboarding = lazy(() => import('./pages/onboarding'));

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="w-full max-w-7xl px-4">
            <LoadingSkeleton />
          </div>
        </div>
      }>
        <RouterRoutes location={location} key={location?.pathname}>
          {/* Public Routes - Redirect to dashboard if authenticated */}
          <Route path="/" element={<PublicRoute><SignIn /></PublicRoute>} />
          <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
          
          {/* Protected Routes - Redirect to signin if not authenticated */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/transaction-management" element={<ProtectedRoute><TransactionManagement /></ProtectedRoute>} />
          <Route path="/systems-management" element={<ProtectedRoute><SystemsManagement /></ProtectedRoute>} />
          <Route path="/reports-analytics" element={<ProtectedRoute><ReportsAnalytics /></ProtectedRoute>} />
          <Route path="/investment-tracker" element={<ProtectedRoute><InvestmentTracker /></ProtectedRoute>} />
          <Route path="/bank-connection" element={<ProtectedRoute><BankConnection /></ProtectedRoute>} />
          <Route path="/receipt-scanner" element={<ProtectedRoute><ReceiptScanner /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/debt-payoff-calculator" element={<ProtectedRoute><DebtPayoffCalculator /></ProtectedRoute>} />
          <Route path="/recurring-bill-tracker" element={<ProtectedRoute><RecurringBillTracker /></ProtectedRoute>} />
          <Route path="/retirement-calculator" element={<ProtectedRoute><RetirementCalculator /></ProtectedRoute>} />
          
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </Suspense>
    </AnimatePresence>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <SkipNavigation />
      <PerformanceMonitor>
        <ErrorBoundary>
          <ScrollToTop />
          <AnimatedRoutes />
        </ErrorBoundary>
      </PerformanceMonitor>
    </BrowserRouter>
  );
};

export default Routes;
